/**
 * ⚠️ **이 폴더(`utils/moments/domain/`)는 ES 응답 → 도메인 타입 순수 변환이다.**
 *
 * ES 응답을 화면이 쓸 도메인 타입으로 옮긴다. ES 어휘(`must`·`filters`·`_source`)를
 * 만들지 않는다 — 그건 형제 폴더 `utils/moments/els/`의 일이다.
 *
 * ⚠️ 브라우저와 서버가 함께 부른다 — 서버는 집계 해석(`server/moments/moment-dashboard` ·
 * `moment-drilldown`)과 MCP 시딩 요약의 ingest(`server/mcp/mcp-summary-seeding`)에 쓴다.
 * 그래서 `'use client'` 와 브라우저 전용 API 를 들이지 않는다.
 */
/**
 * 포스트 **ingest** — 화면이 쓰기 전에 한 번 거친다.
 *
 * 타입 플래그 · 인용/리포스트 정규화 · HTML 제거 · 최신 메트릭 카운트 · 남의 글 텍스트
 * 이동(`borrowed*`). 한 번의 결과가 Excel·피드·컨텐츠 분석으로 갈라지고, 코퍼스는
 * 화면과 Excel이 **같은 배열**을 공유한다. 그래서 소비처별 분기는 여기 없다.
 *
 * ⚠️ 포스트 배열은 `unknown[]` 계약이다. 목록 함수는 제네릭(`<T>`)으로 받은 원소
 * 타입을 그대로 돌려주고, 읽을 때만 `IngestablePost`(이 파일이 읽는 필드만 옵셔널)로
 * 좁힌다. 필드를 더하거나 바꿀 때는 `keep`으로 호출부 타입을 유지한다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

import { log } from 'src/utils/logger';
import { applyParticipantAuthorFields, resolveRawAuthorFromSourceFields } from 'src/utils/moments/domain/post-participant';

const SERVICE_NAME = 'post-ingest';

/** 이 파일이 포스트에서 읽는 것 — 전부 옵셔널이라 어떤 포스트 모양이든 받는다 */
type IngestablePost = {
  id?: unknown;
  _id?: unknown;
  link?: unknown;
  url?: unknown;
  postId?: unknown;
  title?: unknown;
  content?: unknown;
  text?: unknown;
  borrowedTitle?: unknown;
  borrowedContent?: unknown;
  author?: unknown;
  account?: unknown;
  username?: unknown;
  source?: unknown;
  extra?: { reply_to?: unknown } | null;
  isVideo?: unknown;
  isComment?: unknown;
  contentsType?: unknown;
  isRepost?: unknown;
  isQuote?: unknown;
  isQuoted?: unknown;
  originPost?: OriginLike | null;
  _source?: { originPost?: OriginLike | null } | null;
  repostAuthor?: unknown;
  repostAccount?: unknown;
  repostOriginalPost?: unknown;
  repostIndex?: unknown;
  reposts?: RepostItemLike[] | null;
  imageUrl?: unknown;
  imageS3Url?: unknown;
  imagesUrl?: unknown;
  imagesS3Url?: unknown;
  searchLink?: unknown;
  metrics?: Record<string, unknown> | null;
  metricScore?: unknown;
  metricScoreSort?: unknown;
  metricsSort?: unknown;
  likeCount?: unknown;
  commentCount?: unknown;
  viewCount?: unknown;
  savedCount?: unknown;
  shareCount?: unknown;
  downloadCount?: unknown;
  postDate?: unknown;
  date?: unknown;
  timestamp?: unknown;
  createdAt?: unknown;
  _receivedAt?: unknown;
};

type OriginLike = {
  type?: unknown;
  author?: unknown;
  account?: unknown;
  username?: unknown;
  source?: unknown;
  link?: unknown;
  postId?: unknown;
  timestamp?: unknown;
  imageUrl?: unknown;
  imageS3Url?: unknown;
  text?: unknown;
};

type RepostItemLike = {
  repostIndex?: unknown;
  account?: unknown;
  author?: unknown;
};

// 포스트 배열 계약(파일 머리말 ⚠️) — 원소를 바꾸지 않고 읽기용으로만 좁힌다
const asIngestable = (post: unknown): IngestablePost => post as IngestablePost;

/** `applyParticipantAuthorFields`가 받는 모양 — 런타임은 그대로, 필드 타입만 맞춘다 */
const asSocial = (post: unknown): SocialPostInput => post as SocialPostInput;

/** 호출부 원소 타입을 유지한다 — 필드를 더하거나 바꿀 뿐 다른 타입으로 바꾸지 않는다 */
const keep = <T>(post: T, extra: object): T => ({ ...post, ...extra }) as T;

const previewText = (value: unknown): string => (
  typeof value === 'string' ? value.substring(0, 50) : 'N/A'
);

const asOrigin = (value: unknown): OriginLike | null => {
  if (value == null || typeof value !== 'object') return null;
  return value as OriginLike;
};

const asRepostItems = (value: unknown): RepostItemLike[] => (
  Array.isArray(value) ? value : []
);




/**
 * posts 중에서 지정한 account + platform(source) 작성자의 게시물만 반환합니다.
 * connector-profile 계정 검색 결과 등에서 해당 계정이 실제로 작성한 게시물만 필터링할 때 사용합니다.
 * @param {Object[]} posts - 게시물 배열
 * @param {string} account - 계정 식별자 (예: 'centellian24_global')
 * @param {string} platform - 플랫폼(source) (예: 'instagram')
 * @returns {Object[]} account와 source가 일치하는 게시물만 담은 배열
 */
export const filterPostsByAccountAndPlatform = <T,>(
  posts: T[] | null | undefined,
  account: unknown,
  platform: unknown,
): T[] => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return [];
  }
  const accountNorm = account != null ? String(account).trim().toLowerCase() : '';
  const platformNorm = platform != null ? String(platform).trim().toLowerCase() : '';
  if (!accountNorm && !platformNorm) {
    return [...posts];
  }
  return posts.filter((post) => {
    const p = asIngestable(post);
    const postAccount = p.account != null ? String(p.account).trim().toLowerCase() : '';
    const postSource = p.source != null ? String(p.source).trim().toLowerCase() : '';
    const matchAccount = !accountNorm || postAccount === accountNorm;
    const matchPlatform = !platformNorm || postSource === platformNorm;
    return matchAccount && matchPlatform;
  });
};

// 댓글 isComment=true, 인용 isQuote=true, 리포스트 isRepost=true, 
export const getTypedPosts = <T,>(posts: T[] | null | undefined): T[] => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return posts || [];
  }
  return posts.map((post) => {
    const p = asIngestable(post);
    // extra 객체가 존재하고 reply_to 필드에 값이 있을 때만 isComment를 true, 그렇지 않으면 false
    const hasReplyTo = p.extra?.reply_to !== undefined
      && p.extra?.reply_to !== null
      && p.extra?.reply_to !== '';

    // source가 instagram일 때 isVideo 여부에 따라 contentsType 설정
    const source = p.source;
    const isVideo = p.isVideo === true;
    const contentsType = source === 'instagram'
      ? (isVideo ? 'instagram_reels' : 'instagram_post')
      : undefined;

    return keep(post, {
      isComment: hasReplyTo,
      ...(contentsType !== undefined && { contentsType }),
    });
  });
};

/**
 * 리포스트 문서의 상위·일별(metrics) 메트릭 카운트 필드를 0으로 통일합니다.
 * 원본포함(originPost.type === 'repost') 단일 문서와 reposts 복제 행 모두 동일 규칙입니다.
 * @param {Object} post
 * @returns {Object}
 */
function zeroMetricsForRepostDocument<T>(post: T): T {
  if (!post || typeof post !== 'object') return post;

  const p = asIngestable(post);
  const rawMetrics = p.metrics;
  const metrics =
    rawMetrics && typeof rawMetrics === 'object'
      ? Object.fromEntries(
        Object.keys(rawMetrics).map((key) => {
          const day = (rawMetrics as Record<string, unknown>)[key];
          return [
            key,
            {
              ...(day as object),
              likeCount: 0,
              commentCount: 0,
              viewCount: 0,
              savedCount: 0,
              shareCount: 0,
              downloadCount: 0,
            },
          ];
        }),
      )
      : rawMetrics;

  return keep(post, {
    metricScore: 0,
    metricScoreSort: 0,
    // 정렬 시점 스냅샷도 함께 0으로 — 남아 있으면 getMetricsByScoreTime이 그쪽을 읽어
    // 리포스트에 원글 수치가 다시 뜬다.
    metricsSort: {
      likeCount: 0,
      commentCount: 0,
      viewCount: 0,
      savedCount: 0,
      shareCount: 0,
      downloadCount: 0,
    },
    likeCount: 0,
    commentCount: 0,
    viewCount: 0,
    savedCount: 0,
    shareCount: 0,
    downloadCount: 0,
    ...(metrics !== undefined ? { metrics } : {}),
  });
}

/**
 * 리포스트·인용 문서에서 "남의 글" 텍스트를 제거합니다.
 *
 * 수집 데이터는 리포스트의 `title`에 원글 본문을 그대로 복사해 담는 경우가 있어
 * (예: `"유미 reposted #OPEN 💛마이피픈..."`), 그대로 표시하면 리포스터가 직접 쓴 글처럼
 * 보인다. 원문은 `originPost.text`에 있고 화면은 그쪽을 원글 섹션으로 렌더하므로,
 * 이 값들이 남아 있으면 중복·오표시가 된다.
 *
 * | 유형 | 남기는 것 | 비우는 것 |
 * |---|---|---|
 * | 리포스트 | 리포스터 정보 + `originPost` + `link` | `title`·`content` + 미디어·링크 URL |
 * | 인용 | 인용자 정보 + `title`(직접 쓴 코멘트) + 자기 미디어 + `originPost` | `content` |
 *
 * 리포스트는 이미지도 원글 것이 그대로 실려 있어(실측: 루트 `imageS3Url`과
 * `originPost.imageS3Url`이 동일 파일) 지우지 않으면 리포스터가 올린 이미지처럼 보인다.
 * 인용은 인용자가 자기 이미지를 붙일 수 있으므로 미디어를 남긴다.
 * `link`는 "포스트 확인하기" 대상이라 유지한다.
 *
 * `zeroMetricsForRepostDocument`("리포스트는 자기 지표가 없다")와 같은 층의 규칙이다 —
 * 리포스트·인용은 자기 본문도 없다. 채널 무관 적용(인용 플래그는 X·Instagram 모두 존재).
 *
 * 판정은 `isRepost`·`isQuote` 플래그만 본다 — `getIngestedPosts`에서 이 단계보다 먼저
 * `applyQuoteRepostTypeFlags`가 색인 플래그(`isQuoted`)와 `originPost.type`을 두 플래그로
 * 정규화하므로, 여기서 다시 볼 필요가 없다. 리포스트가 인용보다 우선한다(둘은 상호 배타).
 *
 * 텍스트는 지우지 않고 `borrowedTitle`·`borrowedContent`로 옮긴다.
 * Excel 다운로드는 이 텍스트가 **있어야** 한다. 그런데 ingest 시점에는
 * "이 배열을 누가 소비할지"가 정해지지 않는다 — 한 번의 ingest 결과가 Excel·피드·컨텐츠
 * 분석으로 갈라지고, 특히 코퍼스 모드는 Excel과 화면이 **같은 배열**을 공유한다
 * (brand-view의 `filteredBrandMentionPosts`, keyword의 `corpusPostsRef`). API 모드에서도
 * 전건 loader 하나가 Excel과 컨텐츠 분석에 함께 쓰인다(`fetchMonitoringAllPosts`).
 * 그래서 "Excel일 때만 안 지운다"는 ingest 인자로는 표현할 수 없고, 값을 보존해 두고
 * **소비 지점**(excel-export의 `mapPostToRow`)에서만 폴백으로 읽는다.
 * 화면용 `title`·`content`는 빈 값 그대로다.
 *
 * 입력 모양을 보존한 채 `borrowed*` 필드만 더해 돌려준다 — 제네릭이라 호출부에서
 * 타입 단언이 필요 없다(`Moment`를 넣으면 `Moment & {borrowed*}`가 나온다).
 *
 * @template {object} T
 * @param {T} post
 * @returns {T & { borrowedTitle?: string, borrowedContent?: string }}
 *   원본 그대로이거나 해당 필드가 비워진(원문은 `borrowed*`로 이동) 새 객체
 */
export function stripBorrowedTextForQuoteRepost<T extends object>(
  post: T,
): T & { borrowedTitle?: unknown; borrowedContent?: unknown } {
  if (!post || typeof post !== 'object') return post;
  const p = asIngestable(post);
  if (p.isRepost === true) {
    return {
      ...post,
      // `||`로 기존 보존값을 지킨다 — strip이 두 번 돌면(재ingest·미리보기 경유)
      // 두 번째 호출에서 이미 빈 title·content가 보존값을 덮어쓴다.
      borrowedTitle: p.title || p.borrowedTitle || '',
      borrowedContent: p.content || p.borrowedContent || '',
      title: '',
      content: '',
      imageUrl: '',
      imageS3Url: '',
      imagesUrl: [],
      imagesS3Url: [],
      searchLink: [],
    };
  }
  if (p.isQuote === true) {
    return {
      ...post,
      borrowedContent: p.content || p.borrowedContent || '',
      content: '',
    };
  }
  return post;
}

/**
 * originPost / _source.originPost 기반으로 isQuote / isRepost 플래그를 일관되게 생성합니다.
 * reposts[] 복제(펼치기)는 하지 않습니다.
 *
 * @param {Object} post
 * @returns {Object}
 */
function applyQuoteRepostTypeFlags<T>(post: T): T {
  if (!post || typeof post !== 'object') return post;

  const p = asIngestable(post);
  const originRaw = asOrigin(p.originPost ?? p._source?.originPost);
  const hasOrigin = originRaw != null && Object.keys(originRaw).length > 0;

  // ES 색인 루트 플래그(`isQuoted`·`isRepost`)가 있으면 originPost 없이도 판정한다 —
  // `_source`에서 originPost를 뺀 스펙(필드 다이어트)에서도 인용·리포스트가 표시되게.
  // 클라 관례명은 `isQuote`(d 없음)이고 ES 필드명은 `isQuoted`라 둘을 모두 본다.
  const hasRootFlag = p.isQuoted === true || p.isQuote === true || p.isRepost === true;
  if (!hasOrigin && !hasRootFlag) return post;

  const originType = hasOrigin ? originRaw.type : null;
  const isQuote = p.isQuoted === true || p.isQuote === true || originType === 'quote';
  const isRepost = p.isRepost === true || (originType === 'repost' && !isQuote);

  if (!isQuote && !isRepost) return post;

  return keep(post, {
    ...(isQuote ? { isQuote: true } : {}),
    ...(isRepost ? { isRepost: true } : {}),
    ...(p.originPost == null && hasOrigin ? { originPost: originRaw } : {}),
  });
}

/**
 * 리포스트 계열 문서를 UI/테이블 공통 규약으로 맞춥니다.
 *
 * 규약:
 * - originPost: 원문 스니펫(본문·이미지·링크·원 작성자). type이 없으면 repost로 둡니다.
 * - repostAuthor / repostAccount: 리포스트한 주체(ES 단일 문서처럼 루트만 있을 때는 루트에서 승격).
 * - 루트 author / account / username: 항상 원문 작성자(originPost와 동일 축)로 맞춤.
 *
 * @param {Object} post
 * @returns {Object}
 */
function normalizeUnifiedRepostPost<T>(post: T): T {
  if (!post || typeof post !== 'object') return post;

  const p = asIngestable(post);
  const originRaw = asOrigin(p.originPost ?? p._source?.originPost);
  const hasOrigin = originRaw != null && Object.keys(originRaw).length > 0;
  if (!hasOrigin || originRaw == null) return post;

  const originType = originRaw.type;
  const isRepostLike =
    p.isRepost === true
    || (originType === 'repost' && !p.isQuote);

  if (!isRepostLike) return post;

  const reposterName = String(p.repostAuthor ?? '').trim();
  const reposterAcc = String(p.repostAccount ?? '').trim();
  const hasReposterLabel = Boolean(reposterName || reposterAcc);

  const innerAuthor = resolveRawAuthorFromSourceFields({
    author: originRaw.author != null ? String(originRaw.author) : null,
    account: originRaw.account != null ? String(originRaw.account) : null,
    username: originRaw.username != null ? String(originRaw.username) : null,
  }) || p.author;
  const innerAcc =
    originRaw.account
    ?? originRaw.username
    ?? p.account
    ?? p.username;

  const originMerged = {
    ...originRaw,
    type: originType || 'repost',
  };

  let next = keep(post, {
    isRepost: true,
    originPost: originMerged,
  });

  const shouldAlignRootToOrigin =
    hasReposterLabel
    && (originType === 'repost' || Boolean(p.repostOriginalPost));

  if (shouldAlignRootToOrigin) {
    next = keep(next, {
      author: innerAuthor ?? p.author,
      account: innerAcc ?? p.account,
      username: String(innerAcc ?? p.username ?? p.account ?? '').trim() || p.username,
    });
  } else if (
    hasOrigin
    && !p.isQuote
    && (originType === 'repost'
      || (p.isRepost === true && !hasReposterLabel && originType !== 'quote'))
  ) {
    next = keep(next, {
      repostAuthor: p.author ?? '',
      repostAccount: String(p.account ?? p.username ?? '').trim(),
      author: innerAuthor ?? p.author,
      account: String(innerAcc ?? '').trim() || p.account,
      username: String(innerAcc ?? p.username ?? p.account ?? '').trim() || p.username,
    });
  }

  return next;
}

/** 리포스트 복제 행 id 구분자 — 원본 id와 React key 충돌 방지 */
const REPOST_CLONE_ID_SEP = '::repost::';

/**
 * 리포스트 복제 문서 id — 원본 id + 구분자 + 리포스터 식별자
 * @param {Object} basePost - 원본(또는 reposts를 담은) 게시물
 * @param {Object} [repostItem] - reposts[] 항목
 * @param {number} [index=0] - reposts 배열 인덱스
 * @returns {string}
 */
function buildRepostCloneId(
  basePost: unknown,
  repostItem: RepostItemLike | null | undefined = {},
  index = 0,
): string {
  const base = asIngestable(basePost);
  const item = repostItem ?? {};
  const baseId = String(
    base.id || base._id || base.link || 'post',
  ).trim();
  const repostKey = String(
    item.repostIndex
    ?? item.account
    ?? item.author
    ?? index,
  ).trim() || String(index);
  return `${baseId}${REPOST_CLONE_ID_SEP}${repostKey}`;
}

/** 이미 복제된 리포스트 행 id가 원본과 같으면 구분자 id로 교정 */
function ensureDistinctRepostCloneId<T>(post: T, index: number): T {
  const p = asIngestable(post);
  if (!p.isRepost) return post;
  const selfId = String(p.id || '').trim();
  if (selfId.includes(REPOST_CLONE_ID_SEP)) return post;

  const original = p.repostOriginalPost ?? post;
  const orig = asIngestable(original);
  const origId = String(
    orig.id || orig._id || orig.link || '',
  ).trim();
  if (!origId || selfId !== origId) return post;

  return keep(post, {
    id: buildRepostCloneId(original, {
      repostIndex: p.repostIndex,
      account: p.repostAccount,
      author: p.repostAuthor,
    }, index),
  });
}

export const getRepostTemplatePosts = <T,>(posts: T[] | null | undefined): T[] => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return posts || [];
  }

  // 이미 전처리된 배열(리포스트 복제 문서 포함)이라면 중복 생성 방지
  const hasDuplicatedRepostDocs = posts.some((post) => {
    const p = asIngestable(post);
    return Boolean(p.isRepost && p.repostOriginalPost);
  });
  if (hasDuplicatedRepostDocs) {
    return posts.map((item, index) => {
      const n = normalizeUnifiedRepostPost(item);
      const withId = ensureDistinctRepostCloneId(n, index);
      return asIngestable(withId).isRepost ? zeroMetricsForRepostDocument(withId) : withId;
    });
  }

  log.debug('[getRepostTemplatePosts] start', { postsCount: posts?.length });

  const result: T[] = [];

  posts.forEach((post) => {
    const p = asIngestable(post);
    // originPost 확인 (인용 여부 판단)
    const originPostData = asOrigin(p.originPost || p._source?.originPost);
    const hasOriginPost = originPostData != null
      && Object.keys(originPostData).length > 0;

    // reposts 배열 확인 (리포스트 여부 판단)
    const repostItems = asRepostItems(p.reposts);
    const hasReposts = Array.isArray(p.reposts) && repostItems.length > 0;

    const originPostType = hasOriginPost ? originPostData?.type : undefined;

    // 1. originPost.type 기반 분기: quote => 인용, repost => 리포스트
    const addedFields =
      originPostType === 'quote'
        ? { isQuote: true }
        : originPostType === 'repost'
          ? { isRepost: true }
          : {};

    if (hasOriginPost) {
      console.log('[originPost 발견]', {
        postId: p.id || p._id || 'N/A',
        title: previewText(p.title) || 'N/A',
        link: p.link || 'N/A',
        originPost: originPostData,
        originPostType,
        추가된_필드: addedFields,
      });
    }

    const processedPost = keep(post, addedFields);

    // 원본 포스트 추가
    result.push(processedPost);

    // 2. 리포스트 된 게시물인 경우
    if (hasReposts) {
      console.log('[리포스트 발견]', {
        postId: p.id || p._id || 'N/A',
        title: previewText(p.title) || 'N/A',
        link: p.link || 'N/A',
        repostsCount: repostItems.length,
        reposts: p.reposts,
      });

      log.debug('[getRepostTemplatePosts] reposts가 있는 포스트 발견', {
        postId: p.id || p._id || 'N/A',
        title: previewText(p.title) || 'N/A',
        repostsCount: repostItems.length,
        reposts: p.reposts,
      });

      // reposts 배열 크기만큼 포스트 복제 (리포스트는 isQuote 제거하여 isRepost만 유지)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- isQuote는 복제 행에서 제외
      const { isQuote, ...processedWithoutQuote } = asIngestable(processedPost);
      for (let i = 0; i < repostItems.length; i += 1) {
        const repostItem = repostItems[i];
        const repostIndex = repostItem?.repostIndex;

        // 복제 리포스트도 "원본포함" 형태로 정규화:
        // - outer(리포스트한 주체): author/account 를 repostItem 기준으로 덮어쓰기
        // - originPost: 원문(원본 포스트)을 담기
        const originText = p.text || p.content || p.title || '';
        const originImageUrl = p.imageS3Url || p.imageUrl || '';
        const originTimestamp =
          p.timestamp
          || p.postDate
          || p.date
          || null;

        const duplicatedPost = {
          ...processedWithoutQuote,
          id: buildRepostCloneId(post, repostItem, i),
          isRepost: true,
          // 루트 작성자는 원문 작성자와 동일 축(통일 규약). 리포스트한 사람은 repost* 전용.
          author: p.author,
          account: p.account || p.username,
          username: p.username || p.account || p.author,

          // 원문(원본) 정보는 originPost에 보관 (원본포함과 동일한 접근성)
          originPost: {
            type: 'repost',
            source: p.source,
            author: p.author,
            account: p.account || p.username,
            link: p.link,
            postId: p.postId || p.id || p._id,
            timestamp: originTimestamp,
            imageUrl: originImageUrl,
            text: originText,
          },

          // 기존 호환 필드(점진적으로 제거 가능)
          repostAuthor: repostItem?.author,
          repostAccount: repostItem?.account,
          repostOriginalPost: post,
          repostIndex,
        } as T;

        // 복제된 포스트의 추가 필드값 콘솔 출력
        const addedRepostFields = {
          id: asIngestable(duplicatedPost).id,
          isRepost: asIngestable(duplicatedPost).isRepost,
          repostAuthor: asIngestable(duplicatedPost).repostAuthor,
          repostAccount: asIngestable(duplicatedPost).repostAccount,
          repostIndex: asIngestable(duplicatedPost).repostIndex,
        };

        const duplicatedWithZeroMetrics = zeroMetricsForRepostDocument(duplicatedPost);
        const zeroed = asIngestable(duplicatedWithZeroMetrics);

        // 메트릭 필드 0으로 설정된 정보
        const zeroedMetrics = {
          metricScore: zeroed.metricScore,
          likeCount: zeroed.likeCount,
          commentCount: zeroed.commentCount,
          viewCount: zeroed.viewCount,
          savedCount: zeroed.savedCount,
          shareCount: zeroed.shareCount,
          downloadCount: zeroed.downloadCount,
        };

        console.log(`[리포스트 복제 #${i + 1}]`, {
          원본_postId: p.id || p._id || 'N/A',
          원본_title: previewText(p.title) || 'N/A',
          원본_link: p.link || 'N/A',
          복제된_포스트_ID: zeroed.id,
          추가된_필드: addedRepostFields,
          메트릭_0으로_설정: zeroedMetrics,
        });

        result.push(duplicatedWithZeroMetrics);
      }
    }
  });

  log.debug('[getRepostTemplatePosts] end', {
    originalCount: posts.length,
    processedCount: result.length,
    addedCount: result.length - posts.length,
  });

  return result.map((item) => {
    const n = normalizeUnifiedRepostPost(item);
    return asIngestable(n).isRepost ? zeroMetricsForRepostDocument(n) : n;
  });
};




/**
 * post 배열의 모든 게시물의 카운트 필드를 metrics 객체의 최신 날짜 기준 값으로 업데이트
 * @param {Array<Object>} posts - 게시물 배열
 * @returns {Array<Object>} 업데이트된 게시물 배열
 * 
 * @example
 * const updatedPosts = updatePostsCountsFromLatestMetrics(posts);
 */
export const updateCountFromMetrics = <T,>(posts: T[] | null | undefined): T[] => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return posts || [];
  }

  return posts.map((post) => {
    if (!post || typeof post !== 'object') {
      return post;
    }

    const p = asIngestable(post);
    // metrics 객체에서 가장 최신 날짜의 메트릭 추출
    let latestMetrics: Record<string, unknown> | null = null;
    if (p.metrics && typeof p.metrics === 'object' && Object.keys(p.metrics).length > 0) {
      // metrics 객체의 키를 날짜 순으로 정렬하고 가장 최신 값 사용
      const dateKeys = Object.keys(p.metrics).sort().reverse();
      const latestDate = dateKeys[0];
      latestMetrics = (p.metrics as Record<string, unknown>)[latestDate] as Record<string, unknown> | null;
    }

    // 최신 메트릭이 있으면 해당 값 사용, 없으면 기존 필드 값 유지
    return keep(post, {
      likeCount: latestMetrics ? (Number(latestMetrics?.likeCount ?? p.likeCount ?? 0) || 0) : (p.likeCount ?? 0),
      commentCount: latestMetrics ? (Number(latestMetrics?.commentCount ?? p.commentCount ?? 0) || 0) : (p.commentCount ?? 0),
      viewCount: latestMetrics ? (Number(latestMetrics?.viewCount ?? p.viewCount ?? 0) || 0) : (p.viewCount ?? 0),
      savedCount: latestMetrics ? (Number(latestMetrics?.savedCount ?? p.savedCount ?? 0) || 0) : (p.savedCount ?? 0),
      shareCount: latestMetrics ? (Number(latestMetrics?.shareCount ?? p.shareCount ?? 0) || 0) : (p.shareCount ?? 0),
      downloadCount: latestMetrics ? (Number(latestMetrics?.downloadCount ?? p.downloadCount ?? 0) || 0) : (p.downloadCount ?? 0),
    });
  });
};


/**
 * link 기준으로 중복 제거 및 정렬 함수
 * 동일한 link를 가진 포스트 중에서 정렬 기준에 따라 최신/높은 값을 선택
 * 메타데이터(_receivedAt)는 항상 제거됩니다.
 * @param {Array} posts - 포스트 배열
 * @returns {Array} 중복 제거된 포스트 배열
 */
export const getCleanedDuplicatePosts = <T,>(posts: T[] | null | undefined): T[] => {
  //  log.debug('[getCleanedDuplicatePosts]', 'start', { postsCount: posts?.length });

  if (!Array.isArray(posts) || posts.length === 0) return [];

  // link 기준으로 그룹화 (link가 있는 포스트만 처리)
  const linkMap = new Map<string, T[]>();

  posts.forEach((post) => {
    const p = asIngestable(post);
    const link = String(p.link || p.url || '').trim();
    if (!link) return; // link가 없으면 제외

    const group = linkMap.get(link);
    if (!group) {
      linkMap.set(link, [post]);
      return;
    }
    group.push(post);
  });

  // 각 link 그룹에서 최신/높은 값 선택
  const filteredResults: T[] = [];
  linkMap.forEach((groupPosts) => {
    const dropReceivedAt = (post: T): T => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _receivedAt, ...cleanedPost } = asIngestable(post);
      return cleanedPost as T;
    };

    if (groupPosts.length === 1) {
      filteredResults.push(dropReceivedAt(groupPosts[0]));
      return;
    }

    // 동일한 link의 경우 정렬하여 최신/높은 값 선택
    const sorted = [...groupPosts].sort((a, b) => {
      const pa = asIngestable(a);
      const pb = asIngestable(b);
      // 1. postDate 비교 (높은 순)
      const aPostDate = Number(pa.postDate || pa.timestamp || 0);
      const bPostDate = Number(pb.postDate || pb.timestamp || 0);
      if (aPostDate !== bPostDate) {
        return bPostDate - aPostDate;
      }

      // 2. timestamp 비교 (높은 순)
      const aTimestamp = Number(pa.timestamp || 0);
      const bTimestamp = Number(pb.timestamp || 0);
      if (aTimestamp !== bTimestamp) {
        return bTimestamp - aTimestamp;
      }

      // 3. createdAt 비교 (높은 순)
      const aCreatedAt = Number(pa.createdAt || 0);
      const bCreatedAt = Number(pb.createdAt || 0);
      if (aCreatedAt !== bCreatedAt) {
        return bCreatedAt - aCreatedAt;
      }

      // 4. metricScore 비교 (높은 순)
      const aMetricScore = Number(pa.metricScore || 0);
      const bMetricScore = Number(pb.metricScore || 0);
      return bMetricScore - aMetricScore;
    });

    // 가장 높은 값 선택
    filteredResults.push(dropReceivedAt(sorted[0]));
  });

  return filteredResults;
};















/**
 * HTML 태그 제거 + 엔티티 디코딩 (단일 문자열, 공용)
 * <p>, <div> 등 실제 태그만 제거하고, 이모티콘/일반 텍스트의 < > (예: >_<, ꒰՞>·<𓈒՞꒱)는 유지.
 * 연속 공백/개행 처리는 호출부에서 수행.
 * @param {string} html - HTML이 포함된 문자열
 * @returns {string} 태그 제거·엔티티 디코딩된 문자열
 */
export const removeHtmlTagsFromText = (html: unknown): string => {
  if (!html || typeof html !== 'string') return '';

  const withoutTags = html.replace(/<\/?[a-zA-Z][^>]*>/g, '');
  return withoutTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
};

/**
 * HTML 태그를 제거하는 전처리 함수
 * 게시물의 title, content 필드에서 HTML 태그를 제거하고 순수 텍스트만 추출
 * @param {Array} posts - 포스트 배열
 * @returns {Array} HTML 태그가 제거된 포스트 배열
 * 
 * @example
 * preProcessRemoveHtmlTags([{ title: '<p>안녕하세요</p>', content: '<div>텍스트</div>' }])
 * // [{ title: '안녕하세요', content: '텍스트' }]
 */
const getCleanedHtmlPosts = <T,>(posts: T[] | null | undefined): T[] => {
  // log.debug('[preProcessRemoveHtmlTags]', 'start', { postsCount: posts?.length });

  if (!Array.isArray(posts) || posts.length === 0) {
    return posts || [];
  }

  const result = posts.map((post) => {
    const p = asIngestable(post);
    // 원본과 같이 항상 새 객체 — 필드가 안 바뀌어도 참조가 갈린다
    let next = keep(post, {});

    // title에서 HTML 태그 제거 (줄바꿈 \n은 유지, 공백/탭만 한 칸으로)
    if (p.title && typeof p.title === 'string') {
      const cleaned = removeHtmlTagsFromText(p.title);
      next = keep(next, {
        title: cleaned.replace(/\r\n/g, '\n').replace(/[ \t]+/g, ' ').trim(),
      });
    }

    // content에서 HTML 태그 제거 (줄바꿈 \n은 유지, 공백/탭만 한 칸으로)
    if (p.content && typeof p.content === 'string') {
      const cleaned = removeHtmlTagsFromText(p.content);
      next = keep(next, {
        content: cleaned.replace(/\r\n/g, '\n').replace(/[ \t]+/g, ' ').trim(),
      });
    }

    return next;
  });

  // 샘플 데이터 준비 (변경된 항목만, 최대 3개)
  const changedSamples: unknown[] = [];
  for (let i = 0; i < result.length && changedSamples.length < 3; i += 1) {
    const original = asIngestable(posts[i]);
    const processed = asIngestable(result[i]);
    const titleChanged = original.title !== processed.title;
    const contentChanged = original.content !== processed.content;

    if (titleChanged || contentChanged) {
      changedSamples.push({
        index: i + 1,
        title: {
          before: typeof original.title === 'string' ? original.title.substring(0, 80) || 'N/A' : 'N/A',
          after: typeof processed.title === 'string' ? processed.title.substring(0, 80) || 'N/A' : 'N/A',
          changed: titleChanged,
        },
        content: {
          before: typeof original.content === 'string' ? original.content.substring(0, 80) || 'N/A' : 'N/A',
          after: typeof processed.content === 'string' ? processed.content.substring(0, 80) || 'N/A' : 'N/A',
          changed: contentChanged,
        },
      });
    }
  }

  // log.debug('[preProcessRemoveHtmlTags]', 'end', {
  //   processedCount: result.length,
  //   changedCount: changedSamples.length,
  //   samples: changedSamples,
  // });
  return result;
};

/**
 * 게시물 전처리 통합 함수
 * 1. 타입 플래그 (isComment, contentsType 등)
 * 2. HTML 태그 제거 (title, content)
 * 3. 최신 메트릭 기준 카운트 필드 업데이트
 *
 * @param {Array} posts - 원본 포스트 배열
 * @returns {Promise<Array>} 전처리된 포스트 배열
 */
export const getIngestedPosts = async <T,>(
  posts: T[] | null | undefined,
): Promise<T[]> => {
  log.debug(SERVICE_NAME, 'getIngestedPosts 전처리 시작시간 !', { startTime: new Date().toISOString() });

  if (!Array.isArray(posts) || posts.length === 0) {
    // log.debug('[getIngestedPosts]', 'end (empty array)', { postsCount: 0 });
    return [];
  }

  let processedPosts: T[] = [...posts];

  try {
    // 1단계: 중복 제거 (link 기준)
    //  processedPosts = getCleanedDuplicatePosts(processedPosts);

    // isComment, instagram_reels, instagram_post 타입추가
    processedPosts = getTypedPosts(processedPosts);

    // originPost 기반 인용/리포스트 플래그 생성 (복제 없음)
    processedPosts = processedPosts.map((p) => applyQuoteRepostTypeFlags(p));


    // 2단계: 리포스트 복제 데이터 추가 (repostTemplate객체 생성 -> isPosted = true)
    // processedPosts = getRepostTemplatePosts(processedPosts);

    // 3단계: HTML 태그 제거
    processedPosts = getCleanedHtmlPosts(processedPosts);

    // 4단계 : 최신순 메트릭 기준으로 카운트 필드 업데이트
    processedPosts = updateCountFromMetrics(processedPosts);

    // 리포스트 계열: 작성자·originPost 통일 규약 적용
    processedPosts = processedPosts.map((p) => normalizeUnifiedRepostPost(p));

    // 리포스트 문서(원본포함 originPost.type === 'repost' 포함)는 카운트를 표시하지 않음
    processedPosts = processedPosts.map((p) => {
      const item = asIngestable(p);
      const originType = item.originPost?.type ?? item._source?.originPost?.type;
      if (item.isRepost === true || originType === 'repost') {
        return zeroMetricsForRepostDocument(p);
      }
      return p;
    });

    // 리포스트·인용은 "남의 글" 텍스트를 비운다 (원문은 originPost.text로 렌더)
    processedPosts = processedPosts.map((p) => (
      p && typeof p === 'object'
        ? stripBorrowedTextForQuoteRepost(p)
        : p
    ));

    processedPosts = processedPosts.map((p) => {
      const next = applyParticipantAuthorFields(asSocial(p));
      return (next ?? p) as T;
    });

    return processedPosts;
  } catch (error) {
    const err = error as { message?: unknown; stack?: unknown };
    log.fatal('[getIngestedPosts]', '전처리 중 오류 발생', {
      error: err.message,
      stack: err.stack,
      side: 'client',
      toastMessage: '게시물 처리 중 오류가 발생했습니다.',
    });
    // 오류 발생 시 현재까지 처리된 결과 반환 (부분 처리 결과)
    return processedPosts;
  }
};